import { useState, useEffect } from "react";
import { Briefcase, ChevronDown, ChevronUp, Zap } from "lucide-react";

function getStatusStyle(status, isDark) {
  const light = {
    Pending:              { background:"rgba(234,179,8,0.12)",  color:"#d97706",  border:"1px solid rgba(234,179,8,0.25)" },
    Interested:           { background:"rgba(5,150,105,0.12)",  color:"#059669",  border:"1px solid rgba(5,150,105,0.25)" },
    Shortlisted:          { background:"rgba(59,130,246,0.12)", color:"#2563eb",  border:"1px solid rgba(59,130,246,0.25)" },
    "Interview Scheduled":{ background:"rgba(168,85,247,0.12)", color:"#9333ea",  border:"1px solid rgba(168,85,247,0.25)" },
    "Offer Received":     { background:"rgba(5,150,105,0.12)",  color:"#059669",  border:"1px solid rgba(5,150,105,0.25)" },
    Rejected:             { background:"rgba(239,68,68,0.12)",  color:"#dc2626",  border:"1px solid rgba(239,68,68,0.25)" },
    Screened:             { background:"rgba(100,116,139,0.12)",color:"#64748b",  border:"1px solid rgba(100,116,139,0.25)" },
    Screening:            { background:"rgba(100,116,139,0.12)",color:"#64748b",  border:"1px solid rgba(100,116,139,0.25)" },
  };
  const dark = {
    Pending:              { background:"rgba(234,179,8,0.12)",  color:"#fbbf24",  border:"1px solid rgba(234,179,8,0.25)" },
    Interested:           { background:"rgba(5,150,105,0.12)",  color:"#34d399",  border:"1px solid rgba(5,150,105,0.25)" },
    Shortlisted:          { background:"rgba(59,130,246,0.12)", color:"#60a5fa",  border:"1px solid rgba(59,130,246,0.25)" },
    "Interview Scheduled":{ background:"rgba(168,85,247,0.12)", color:"#c084fc",  border:"1px solid rgba(168,85,247,0.25)" },
    "Offer Received":     { background:"rgba(5,150,105,0.12)",  color:"#34d399",  border:"1px solid rgba(5,150,105,0.25)" },
    Rejected:             { background:"rgba(239,68,68,0.12)",  color:"#f87171",  border:"1px solid rgba(239,68,68,0.25)" },
    Screened:             { background:"rgba(100,116,139,0.12)",color:"#94a3b8",  border:"1px solid rgba(100,116,139,0.25)" },
    Screening:            { background:"rgba(100,116,139,0.12)",color:"#94a3b8",  border:"1px solid rgba(100,116,139,0.25)" },
  };
  const fallbackL = { background:"rgba(0,0,0,0.04)",     color:"#64748b",            border:"1px solid rgba(0,0,0,0.1)" };
  const fallbackD = { background:"rgba(255,255,255,0.05)",color:"rgba(255,255,255,0.5)",border:"1px solid rgba(255,255,255,0.1)" };
  return isDark ? (dark[status] || fallbackD) : (light[status] || fallbackL);
}

function meritColor(s) {
  if (s == null) return "#94a3b8";
  return s >= 80 ? "#059669" : s >= 60 ? "#f97316" : "#dc2626";
}

function MeritBadge({ score, isDark }) {
  const c = meritColor(score);
  return (
    <div data-testid="merit-badge" style={{ width:44,height:44,borderRadius:"50%",border:`2px solid ${c}`,background:isDark?"rgba(255,255,255,0.06)":"#f8f9fc",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
      <span style={{ fontSize:13,fontWeight:700,color:c,lineHeight:1 }}>{score ?? "—"}</span>
      {score != null && <span style={{ fontSize:9,color:c,opacity:0.8,lineHeight:1 }}>/100</span>}
    </div>
  );
}

const BK_LABELS = { skill_match:"Skills", experience:"Exp", verified:"Verified", portfolio:"Portfolio" };

function ScoreBreakdown({ breakdown, isDark, t }) {
  return (
    <div style={{ marginTop:10,display:"flex",flexDirection:"column",gap:5 }} data-testid="score-breakdown">
      {["skill_match","experience","verified","portfolio"].map((k) => {
        const raw = breakdown[k]; if (raw == null) return null;
        const pct = Math.round(raw * 100); const c = meritColor(pct);
        return (
          <div key={k} style={{ display:"flex",alignItems:"center",gap:8 }}>
            <span style={{ fontSize:11,color:t.textMuted,width:52,flexShrink:0 }}>{BK_LABELS[k]}</span>
            <div style={{ flex:1,height:5,borderRadius:3,background:isDark?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.08)",overflow:"hidden" }}>
              <div style={{ width:`${pct}%`,height:"100%",borderRadius:3,background:c,transition:"width 0.6s ease" }} />
            </div>
            <span style={{ fontSize:11,color:c,fontWeight:600,width:28,textAlign:"right" }}>{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function ApplicationCard({ app, isDark, t, isExpanded, onToggle, justUpdated }) {
  const initial = (app.company_name || "?")[0].toUpperCase();
  const title = app.job_title || app.role || "Role";
  const dateStr = (app.updated_at || app.created_at)
    ? new Date(app.updated_at || app.created_at).toLocaleDateString("en-IN",{ day:"numeric",month:"short" }) : "";
  const snippet = app.explanation?.length > 80 ? app.explanation.slice(0,80) + "..." : app.explanation;

  return (
    <div data-testid={`app-card-${app.id}`} style={{ ...t.glassCard,borderRadius:12,padding:"14px 16px",position:"relative",transition:"box-shadow 0.2s" }}>
      <div style={{ display:"flex",alignItems:"flex-start",gap:12 }}>
        <div style={{ width:40,height:40,borderRadius:10,background:"linear-gradient(135deg,#1a1f5e 0%,#2d3494 100%)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:16,flexShrink:0,fontFamily:"Cabinet Grotesk,Plus Jakarta Sans,sans-serif" }}>
          {initial}
        </div>
        <div style={{ flex:1,minWidth:0 }}>
          <div style={{ display:"flex",alignItems:"center",gap:8,flexWrap:"wrap" }}>
            <span style={{ fontFamily:"Cabinet Grotesk,Plus Jakarta Sans,sans-serif",fontWeight:700,fontSize:14,color:t.textPrimary,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:180 }}>{title}</span>
            <span style={{ fontSize:12,color:t.textMuted }}>·</span>
            <span style={{ fontSize:12,color:t.textSecondary }}>{app.company_name}</span>
            {justUpdated && <span data-testid="live-dot" style={{ width:8,height:8,borderRadius:"50%",background:"#10b981",display:"inline-block",animation:"wsc-pulse 1.5s infinite",flexShrink:0 }} />}
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:8,marginTop:6,flexWrap:"wrap" }}>
            <span data-testid={`status-badge-${app.id}`} style={{ ...getStatusStyle(app.status,isDark),fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:20 }}>
              {app.status || "Pending"}
            </span>
            {dateStr && <span style={{ fontSize:11,color:t.textMuted }}>{dateStr}</span>}
          </div>
        </div>
        {app.merit_score != null && <MeritBadge score={app.merit_score} isDark={isDark} />}
      </div>

      {app.score_breakdown && <ScoreBreakdown breakdown={app.score_breakdown} isDark={isDark} t={t} />}

      {app.explanation && (
        <div style={{ marginTop:10 }}>
          <button data-testid={`expand-explanation-${app.id}`} onClick={onToggle} style={{ display:"flex",alignItems:"center",gap:5,background:"none",border:"none",cursor:"pointer",padding:0,color:t.accent||"#f97316" }}>
            <Zap size={12} />
            <span style={{ fontSize:11,fontWeight:600,fontFamily:"Inter,sans-serif" }}>AI Insight</span>
            {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          <p style={{ marginTop:5,fontSize:12,color:t.textSecondary,lineHeight:1.6,fontFamily:"Inter,sans-serif" }}>
            {isExpanded ? app.explanation : snippet}
          </p>
        </div>
      )}
    </div>
  );
}

export default function ApplicationTimeline({ applications: initialApps, isDark, t }) {
  const [apps, setApps] = useState(initialApps || []);
  const [expanded, setExpanded] = useState({});
  const [updatedIds, setUpdatedIds] = useState({});

  useEffect(() => {
    // EventSource doesn't support custom headers; token is sent as query param.
    // Backend get_current_user checks cookies first (works when logged in via cookie).
    // To support header-less SSE auth, add a ?token= check in the backend SSE route.
    const token = sessionStorage.getItem("wonstep_token");
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const es = new EventSource(`${base}/api/applications/mine/stream?token=${token}`);
    es.addEventListener("status_change", (e) => {
      try {
        const data = JSON.parse(e.data);
        setApps((prev) => prev.map((a) => a.id === data.application_id ? { ...a, status: data.new_stage } : a));
        setUpdatedIds((prev) => ({ ...prev, [data.application_id]: true }));
        setTimeout(() => setUpdatedIds((prev) => { const n={...prev}; delete n[data.application_id]; return n; }), 4000);
      } catch (_) {}
    });
    es.onerror = () => es.close();
    return () => es.close();
  }, []);

  useEffect(() => { setApps(initialApps || []); }, [initialApps]);

  if (!apps.length) return (
    <div className="py-12 text-center" data-testid="no-applications">
      <Briefcase size={36} className="mx-auto mb-3" style={{ color: t.textMuted }} />
      <p className="text-sm" style={{ color: t.textMuted }}>No applications yet. You'll appear here once a company picks you up.</p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="application-timeline">
      <style>{`@keyframes wsc-pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
      {apps.map((app) => (
        <ApplicationCard key={app.id} app={app} isDark={isDark} t={t}
          isExpanded={!!expanded[app.id]}
          onToggle={() => setExpanded((prev) => ({ ...prev, [app.id]: !prev[app.id] }))}
          justUpdated={!!updatedIds[app.id]}
        />
      ))}
    </div>
  );
}
